/* eslint-disable react/prop-types */
import { useEffect, useState } from 'react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { apiRequest } from '../../Services/api';
import { cloneCateringContent } from '../CateringPage/cateringContent';

const EMPTY_PACKAGE = { name: '', pricePerPerson: '', minimumGuests: 40, minimumTotal: '', description: '' };
const EMPTY_TRAVEL_ROW = { label: '', price: '', minimumMiles: '', maximumMiles: '', fee: '' };
const EMPTY_ADD_ON = { label: '', price: '', pricingMode: 'per_unit', unitPrice: '', minimumPrice: '', maximumPrice: '', unitLabel: 'item' };

function TravelRows({ rows, onChange, onAdd, onRemove }) {
  return (
    <fieldset className='admin-catering-repeater'>
      <legend>Travel</legend>
      {rows.map((row, index) => (
        <div className='admin-catering-price-row admin-catering-travel-row' key={`travel-${row.id || index}`}>
          <label>
            Label
            <input required maxLength='120' value={row.label} onChange={(event) => onChange(index, 'label', event.target.value)} />
          </label>
          <label>
            Displayed price
            <input required maxLength='80' value={row.price} onChange={(event) => onChange(index, 'price', event.target.value)} placeholder='+$50' />
          </label>
          <label>From miles<input required min='0' max='999.9' step='0.1' type='number' value={row.minimumMiles} onChange={(event) => onChange(index, 'minimumMiles', event.target.value)} /></label>
          <label>Through miles<input required min='0' max='999.9' step='0.1' type='number' value={row.maximumMiles} onChange={(event) => onChange(index, 'maximumMiles', event.target.value)} /></label>
          <label>Numeric fee<input required min='0' max='999999.99' step='0.01' type='number' value={row.fee} onChange={(event) => onChange(index, 'fee', event.target.value)} /></label>
          <button className='admin-icon-button' type='button' onClick={() => onRemove(index)} disabled={rows.length === 1} aria-label={`Remove travel row ${index + 1}`}>
            <FiTrash2 />
          </button>
        </div>
      ))}
      <button className='admin-text-button' type='button' onClick={onAdd}><FiPlus /> Add travel row</button>
    </fieldset>
  );
}

function AddOnRows({ rows, onChange, onAdd, onRemove }) {
  return (
    <fieldset className='admin-catering-repeater'>
      <legend>Add-ons</legend>
      {rows.map((row, index) => (
        <div className='admin-catering-add-on-row' key={`add-on-${row.id || index}`}>
          <label>Label<input required maxLength='120' value={row.label} onChange={(event) => onChange(index, 'label', event.target.value)} /></label>
          <label>Displayed price<input required maxLength='80' value={row.price} onChange={(event) => onChange(index, 'price', event.target.value)} /></label>
          <label>Pricing method<select value={row.pricingMode} onChange={(event) => onChange(index, 'pricingMode', event.target.value)}><option value='flat'>Flat</option><option value='per_person'>Per person</option><option value='per_unit'>Per unit</option><option value='custom_range'>Custom range</option></select></label>
          {row.pricingMode === 'custom_range' ? <>
            <label>Minimum price<input required min='0' step='0.01' type='number' value={row.minimumPrice ?? ''} onChange={(event) => onChange(index, 'minimumPrice', event.target.value)} /></label>
            <label>Maximum price<input required min='0' step='0.01' type='number' value={row.maximumPrice ?? ''} onChange={(event) => onChange(index, 'maximumPrice', event.target.value)} /></label>
          </> : <label>Unit price<input required min='0' step='0.01' type='number' value={row.unitPrice ?? ''} onChange={(event) => onChange(index, 'unitPrice', event.target.value)} /></label>}
          <label>Unit label<input required maxLength='50' value={row.unitLabel} onChange={(event) => onChange(index, 'unitLabel', event.target.value)} /></label>
          <button className='admin-icon-button' type='button' onClick={() => onRemove(index)} disabled={rows.length === 1} aria-label={`Remove add-on row ${index + 1}`}><FiTrash2 /></button>
        </div>
      ))}
      <button className='admin-text-button' type='button' onClick={onAdd}><FiPlus /> Add add-on row</button>
    </fieldset>
  );
}

function PackageRows({ packages, onChange, onAdd, onRemove }) {
  return (
    <div className='admin-catering-packages'>
      {packages.map((cateringPackage, index) => (
        <fieldset className='admin-catering-package' key={`package-${cateringPackage.id || index}`}>
          <legend>Package {index + 1}</legend>
          <div className='admin-catering-form-grid'>
            <label className='admin-catering-span-2'>
              Package name
              <input required maxLength='120' value={cateringPackage.name} onChange={(event) => onChange(index, 'name', event.target.value)} />
            </label>
            <label>
              Price per person
              <input required min='0' max='999999.99' step='0.01' type='number' value={cateringPackage.pricePerPerson} onChange={(event) => onChange(index, 'pricePerPerson', event.target.value)} />
            </label>
            <label>
              Minimum guests
              <input required min='1' max='10000' step='1' type='number' value={cateringPackage.minimumGuests} onChange={(event) => onChange(index, 'minimumGuests', event.target.value)} />
            </label>
            <label>
              Starting total
              <input required min='0' max='999999.99' step='0.01' type='number' value={cateringPackage.minimumTotal} onChange={(event) => onChange(index, 'minimumTotal', event.target.value)} />
            </label>
            <label className='admin-catering-span-2'>
              Package description
              <textarea required maxLength='600' rows='3' value={cateringPackage.description} onChange={(event) => onChange(index, 'description', event.target.value)} />
            </label>
          </div>
          <button className='admin-text-button admin-danger-text' type='button' onClick={() => onRemove(index)} disabled={packages.length === 1}><FiTrash2 /> Remove package</button>
        </fieldset>
      ))}
      <button className='admin-button admin-button-outline' type='button' onClick={onAdd} disabled={packages.length >= 8}><FiPlus /> Add package</button>
    </div>
  );
}

export default function CateringEditor({ content, loading, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content) setForm(cloneCateringContent(content));
  }, [content]);

  if (loading && !form) return <div className='admin-gate admin-inline-gate'>Loading catering content…</div>;
  if (!form) return <p className='admin-empty'>Catering content could not be loaded.</p>;

  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updateList = (field, index, key, value) => {
    setForm((current) => ({
      ...current,
      [field]: current[field].map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row),
    }));
  };
  const addListRow = (field, row) => setForm((current) => ({ ...current, [field]: [...current[field], { ...row }] }));
  const removeListRow = (field, index) => setForm((current) => ({ ...current, [field]: current[field].filter((_, rowIndex) => rowIndex !== index) }));

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const data = await apiRequest('/catering/index.php', { method: 'PUT', body: JSON.stringify(form) });
      const saved = cloneCateringContent(data.catering);
      setForm(saved);
      onSaved(saved);
      toast.success('Catering page updated.');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className='admin-catering-editor' onSubmit={submit}>
      <div className='admin-section-heading'>
        <div><h1>Catering page</h1><p>Edit the public packages, pricing, travel fees, contact details, and search metadata.</p></div>
        <a className='admin-button admin-button-outline' href='/catering' target='_blank' rel='noreferrer'>Preview page</a>
      </div>

      <section className='admin-form-panel admin-catering-section'>
        <div className='admin-section-heading'><div><h2>Hero and search details</h2><p>The main message and metadata shown to visitors and search engines.</p></div></div>
        <div className='admin-catering-form-grid'>
          <label className='admin-catering-span-2'>Hero heading<input required maxLength='180' value={form.heroHeading} onChange={(event) => setField('heroHeading', event.target.value)} /></label>
          <label>Highlighted phrase<input required maxLength='80' value={form.heroHighlight} onChange={(event) => setField('heroHighlight', event.target.value)} /></label>
          <label className='admin-catering-span-2'>Hero description<textarea required maxLength='2000' rows='4' value={form.heroDescription} onChange={(event) => setField('heroDescription', event.target.value)} /></label>
          <label className='admin-catering-span-2'>Starting-price line<input required maxLength='180' value={form.heroStartingPrice} onChange={(event) => setField('heroStartingPrice', event.target.value)} /></label>
          <label className='admin-catering-span-2'>SEO title<input required maxLength='180' value={form.seoTitle} onChange={(event) => setField('seoTitle', event.target.value)} /></label>
          <label className='admin-catering-span-2'>SEO description<textarea required maxLength='320' rows='3' value={form.seoDescription} onChange={(event) => setField('seoDescription', event.target.value)} /></label>
        </div>
      </section>

      <section className='admin-form-panel admin-catering-section'>
        <div className='admin-section-heading'><div><h2>Peanut bar packages</h2><p>Packages appear in this order on the public page.</p></div></div>
        <div className='admin-catering-form-grid admin-catering-section-copy'>
          <label>Section heading<input required maxLength='160' value={form.packagesHeading} onChange={(event) => setField('packagesHeading', event.target.value)} /></label>
          <label className='admin-catering-span-2'>Section introduction<textarea required maxLength='2000' rows='3' value={form.packagesIntro} onChange={(event) => setField('packagesIntro', event.target.value)} /></label>
          <label className='admin-catering-span-2'>Large-event note<input required maxLength='255' value={form.largeEventNote} onChange={(event) => setField('largeEventNote', event.target.value)} /></label>
        </div>
        <PackageRows
          packages={form.packages}
          onChange={(index, key, value) => updateList('packages', index, key, value)}
          onAdd={() => addListRow('packages', EMPTY_PACKAGE)}
          onRemove={(index) => removeListRow('packages', index)}
        />
      </section>

      <section className='admin-form-panel admin-catering-section'>
        <div className='admin-section-heading'><div><h2>Travel and add-ons</h2><p>Use display prices for ranges or labels such as “Included.”</p></div></div>
        <label>Section heading<input required maxLength='160' value={form.detailsHeading} onChange={(event) => setField('detailsHeading', event.target.value)} /></label>
        <div className='admin-catering-repeater-grid'>
          <TravelRows
            rows={form.travel}
            onChange={(index, key, value) => updateList('travel', index, key, value)}
            onAdd={() => addListRow('travel', EMPTY_TRAVEL_ROW)}
            onRemove={(index) => removeListRow('travel', index)}
          />
          <AddOnRows
            rows={form.addOns}
            onChange={(index, key, value) => updateList('addOns', index, key, value)}
            onAdd={() => addListRow('addOns', EMPTY_ADD_ON)}
            onRemove={(index) => removeListRow('addOns', index)}
          />
        </div>
        <label>Market-pricing note<textarea required maxLength='2000' rows='4' value={form.marketNote} onChange={(event) => setField('marketNote', event.target.value)} /></label>
      </section>

      <section className='admin-form-panel admin-catering-section'>
        <div className='admin-section-heading'><div><h2>Contact details</h2><p>These values power both visible contact buttons and structured data.</p></div></div>
        <div className='admin-catering-form-grid'>
          <label className='admin-catering-span-2'>Contact heading<input required maxLength='160' value={form.contactHeading} onChange={(event) => setField('contactHeading', event.target.value)} /></label>
          <label className='admin-catering-span-2'>Contact description<textarea required maxLength='2000' rows='4' value={form.contactDescription} onChange={(event) => setField('contactDescription', event.target.value)} /></label>
          <label>Phone<input required maxLength='30' inputMode='tel' value={form.phone} onChange={(event) => setField('phone', event.target.value)} /></label>
          <label>Email<input required maxLength='255' type='email' value={form.email} onChange={(event) => setField('email', event.target.value)} /></label>
          <label className='admin-catering-span-2'>Service area<input required maxLength='255' value={form.serviceArea} onChange={(event) => setField('serviceArea', event.target.value)} /></label>
        </div>
      </section>

      <section className='admin-form-panel admin-catering-section'>
        <div className='admin-section-heading'><div><h2>Invoice defaults</h2><p>These details appear in catering invoice emails and PDF attachments.</p></div></div>
        <div className='admin-catering-form-grid'>
          <label className='admin-catering-span-2'>Payment terms<input required maxLength='255' value={form.invoicePaymentTerms} onChange={(event) => setField('invoicePaymentTerms', event.target.value)} /></label>
          <label className='admin-catering-span-2'>Invoice footer<textarea required maxLength='500' rows='3' value={form.invoiceFooterNote} onChange={(event) => setField('invoiceFooterNote', event.target.value)} /></label>
        </div>
      </section>

      <div className='admin-catering-save-bar'>
        <p>Changes go live on the catering page immediately after saving.</p>
        <button className='admin-button admin-button-primary' type='submit' disabled={saving}>{saving ? 'Saving…' : 'Save catering page'}</button>
      </div>
    </form>
  );
}
